import { IonButton } from '@ionic/react';
import { MouseEventHandler, PropsWithChildren } from 'react';

import styles from '@/components/Button/button.module.css';
import { Icon, IconName } from '@/components/Icon';
import { IconRotate, IconSize } from '@/components/Icon/types';

import { mergeClassNames } from '@/utils';

import type { JSX as IonicJSX } from '@ionic/core';

// This allows us to get the props of the IonButton so that the Button can be used as a drop-in replacement
type IonButtonProps = IonicJSX.IonButton;

export interface ButtonProps extends Omit<IonButtonProps, 'size' | 'expand'> {
  size?: 'small' | 'medium' | 'large';
  variant?: 'primary' | 'secondary' | 'tertiary' | 'danger';
  leadingIcon?: IconName;
  leadingIconDirection?: IconRotate;
  trailingIcon?: IconName;
  trailingIconDirection?: IconRotate;
  className?: string;
  isFullWidth?: boolean;
  isLoading?: boolean;
  onClick?: MouseEventHandler<HTMLIonButtonElement>;
  id?: string;
}

export const Button = ({
  children,
  variant = 'primary',
  size = 'medium',
  leadingIcon,
  leadingIconDirection,
  trailingIcon,
  trailingIconDirection,
  type,
  isFullWidth,
  disabled,
  isLoading,
  onClick,
  id,
  href,
  ...props
}: PropsWithChildren<ButtonProps>) => {
  const iconSizesSwitch = {
    small: IconSize.extraSmall,
    medium: IconSize.medium,
    large: IconSize.large,
  };

  return (
    <IonButton
      className={mergeClassNames(
        styles.button,
        styles[variant],
        styles[size],
        isLoading && styles.loading,
        disabled && styles.disabled,
        isFullWidth && styles.fullWidth,
        props.className,
      )}
      disabled={disabled}
      {...(id ? { id } : {})}
      {...(href
        ? { href, ...(props.target === '_blank' ? { target: '_blank', rel: 'noopener noreferrer' } : {}) }
        : {
            onClick,
          })}
      {...(type ? { type } : {})}>
      <div className={styles[`inner-button-${size}`]}>
        {isLoading ? (
          <Icon className={styles.spinner} color='#90C953' name='spinner' />
        ) : (
          <>
            {leadingIcon && <Icon direction={leadingIconDirection} name={leadingIcon} size={iconSizesSwitch[size]} />}
            {children}
            {trailingIcon && (
              <Icon direction={trailingIconDirection} name={trailingIcon} size={iconSizesSwitch[size]} />
            )}
          </>
        )}
      </div>
    </IonButton>
  );
};
